import { create } from 'zustand'
import type { TenantTheme, TenantBrandingCache } from '@/types/tenant'
import type { DomainSettings } from '@/api/settings'
import { getContrastForeground, getDirection } from '@/utils/colorUtils'

const CACHE_KEY     = 'telematics_branding_v2'  // bumped → clears old blue-theme cache
const CACHE_TTL     = 24 * 60 * 60 * 1000        // 24 hours
export const USER_COLOR_KEY = 'user_theme_primary' // user's custom primary colour (cleared on logout)

export const DEFAULT_THEME: TenantTheme = {
  primaryColor: '#53C9C0',
  primaryForeground: '#FFFFFF',
  appName: 'Telematics',
  logoUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  fontFamily: null,
  borderRadius: 'md',
  locale: 'en',
  direction: 'ltr',
}

interface TenantState {
  theme: TenantTheme | null
  /** Operational settings from GET /settings (timezone, units, etc.) */
  settings: DomainSettings | null
  /** Apply a partial override and recompute derived fields (foreground, direction) */
  setTheme: (override: Partial<TenantTheme>) => void
  /** Load previously cached branding from localStorage. Called once on app mount. */
  loadFromCache: () => void
  /** Persist current theme to localStorage */
  saveToCache: (theme: TenantTheme) => void
  /**
   * Parse the raw GET /settings response and apply any theme sub-object.
   * Also stores the full settings object for use by formatters.
   * Handles missing/invalid keys gracefully.
   */
  applySettingsResponse: (raw: Record<string, unknown>) => void
  /** Apply and persist a user-chosen primary colour. Survives page refresh; cleared on logout. */
  setPrimaryColor: (hex: string) => void
}

export const useTenantStore = create<TenantState>((set, get) => ({
  theme: null,
  settings: null,

  setTheme: (override) =>
    set((state) => {
      const base = state.theme ?? DEFAULT_THEME
      const primaryColor = override.primaryColor ?? base.primaryColor
      const locale = override.locale ?? base.locale
      const theme: TenantTheme = {
        ...base,
        ...override,
        primaryColor,
        primaryForeground:
          override.primaryForeground ?? getContrastForeground(primaryColor),
        locale,
        direction: override.locale ? getDirection(locale) : base.direction,
      }
      return { theme }
    }),

  loadFromCache: () => {
    try {
      const raw = localStorage.getItem(CACHE_KEY)
      if (!raw) {
        set({ theme: DEFAULT_THEME })
      } else {
        const cache = JSON.parse(raw) as TenantBrandingCache
        if (Date.now() - cache.cachedAt > CACHE_TTL) {
          set({ theme: DEFAULT_THEME })
        } else {
          set({ theme: cache.theme })
        }
      }
    } catch {
      set({ theme: DEFAULT_THEME })
    }
    // Always re-apply user's custom colour on top (survives page refresh)
    const userColor = localStorage.getItem(USER_COLOR_KEY)
    if (userColor) get().setTheme({ primaryColor: userColor })
  },

  saveToCache: (theme) => {
    const cache: TenantBrandingCache = { theme, cachedAt: Date.now() }
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
    } catch {
      // localStorage may be full or unavailable (private browsing)
    }
  },

  applySettingsResponse: (raw) => {
    // The API returns settings as a free-form JSON blob; theme fields live under .theme
    const themeRaw = raw?.theme as Record<string, unknown> | undefined
    if (!themeRaw) return

    const override: Partial<TenantTheme> = {}
    if (typeof themeRaw.primaryColor === 'string')
      override.primaryColor = themeRaw.primaryColor
    if (typeof themeRaw.primaryForeground === 'string')
      override.primaryForeground = themeRaw.primaryForeground
    if (typeof themeRaw.appName === 'string') override.appName = themeRaw.appName
    if (themeRaw.logoUrl === null || typeof themeRaw.logoUrl === 'string')
      override.logoUrl = themeRaw.logoUrl as string | null
    if (themeRaw.logoDarkUrl === null || typeof themeRaw.logoDarkUrl === 'string')
      override.logoDarkUrl = themeRaw.logoDarkUrl as string | null
    if (themeRaw.faviconUrl === null || typeof themeRaw.faviconUrl === 'string')
      override.faviconUrl = themeRaw.faviconUrl as string | null
    if (themeRaw.fontFamily === null || typeof themeRaw.fontFamily === 'string')
      override.fontFamily = themeRaw.fontFamily as string | null
    if (typeof themeRaw.borderRadius === 'string')
      override.borderRadius = themeRaw.borderRadius as TenantTheme['borderRadius']
    if (typeof themeRaw.locale === 'string') override.locale = themeRaw.locale

    get().setTheme(override)
    const current = get().theme
    if (current) get().saveToCache(current)

    // Store the full settings object (without theme) for formatters
    const { theme: _theme, ...rest } = raw
    void _theme
    set({ settings: rest as DomainSettings })

    // User colour always wins — re-apply on top of API settings
    const userColor = localStorage.getItem(USER_COLOR_KEY)
    if (userColor) get().setTheme({ primaryColor: userColor })
  },

  setPrimaryColor: (hex) => {
    get().setTheme({ primaryColor: hex })
    try { localStorage.setItem(USER_COLOR_KEY, hex) } catch { /* noop */ }
  },
}))
